"use client";

import { c as _c } from "react/compiler-runtime";
import { type FC, useEffect, useState } from "react";
import { sessionCookieHandler } from "@/app/[domain]/(sites)/_actions/session/session-cookie-handler";
import { PaywallLoadingSkeleton } from "@/components/Paywall/_components/PaywallLoadingSkeleton/PaywallLoadingSkeleton.component";
import { AuthenticatedPaywall } from "@/components/Paywall/AuthenticatedPaywall.component";
import { AuthenticatedTrialFocusPaywall } from "@/components/Paywall/AuthenticatedTrialFocusPaywall.component";
import { UnauthenticatedPaywall } from "@/components/Paywall/UnauthenticatedPaywall.component";
import { UnauthenticatedTrialFocusPaywall } from "@/components/Paywall/UnauthenticatedTrialFocusPaywall.component";
import { useCookiebotConsent } from "@/vendors/Cookiebot/_hooks/use-cookiebot-consent.hook";
import { ObservedElement } from "@/vendors/Snowplow/components/ObservedElement.component";
type PaywallDeciderProps = {
  readonly isLoggedIn: boolean;
  readonly hasTrialFocus: boolean;
  readonly isTrialExpired: boolean;
};
const numberOfSessionsForTrialFocus = 3;
const paywallVariants = {
  authenticated: AuthenticatedPaywall,
  "authenticated-trial-focus": AuthenticatedTrialFocusPaywall,
  unauthenticated: UnauthenticatedPaywall,
  "unauthenticated-trial-focus": UnauthenticatedTrialFocusPaywall
} as const satisfies Record<string, FC>;
type PaywallVariant = keyof typeof paywallVariants;
type PaywallState = {
  readonly status: "loading";
} | {
  readonly status: "resolved";
  readonly variant: PaywallVariant;
};
export function PaywallDecider(t0) {
  const $ = _c(13);
  const {
    isLoggedIn,
    hasTrialFocus,
    isTrialExpired
  } = t0;
  const cookiebotConsent = useCookiebotConsent();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      status: "loading"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [paywallState, setPaywallState] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== cookiebotConsent || $[2] !== hasTrialFocus || $[3] !== isLoggedIn || $[4] !== isTrialExpired) {
    t2 = () => {
      const sessionCount = sessionCookieHandler({
        CookieConsent: cookiebotConsent
      });
      const isSessionIndicatingTrialFocus = sessionCount !== null && sessionCount < numberOfSessionsForTrialFocus;
      const variant = selectPaywallVariant({
        isCurrentUserTrialExpired: isTrialExpired,
        isLoggedIn,
        isSessionIndicatingTrialFocus,
        isSiteWithTrialFocus: hasTrialFocus
      });
      setPaywallState({
        status: "resolved",
        variant
      });
    };
    t3 = [cookiebotConsent, hasTrialFocus, isLoggedIn, isTrialExpired];
    $[1] = cookiebotConsent;
    $[2] = hasTrialFocus;
    $[3] = isLoggedIn;
    $[4] = isTrialExpired;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  if (paywallState.status === "loading") {
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="print:hidden"><PaywallLoadingSkeleton /></div>;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  const PaywallComponent = paywallVariants[paywallState.variant];
  let t4;
  if ($[8] !== PaywallComponent) {
    t4 = <PaywallComponent />;
    $[8] = PaywallComponent;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== paywallState.variant || $[11] !== t4) {
    t5 = <ObservedElement className="print:hidden" content={paywallState.variant} elementId={paywallState.variant} type="paywall">{t4}</ObservedElement>;
    $[10] = paywallState.variant;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
type PaywallSelection = {
  readonly isLoggedIn: boolean;
  readonly isSiteWithTrialFocus: boolean;
  readonly isCurrentUserTrialExpired: boolean;
  readonly isSessionIndicatingTrialFocus: boolean;
};
function selectPaywallVariant({
  isLoggedIn,
  isSiteWithTrialFocus,
  isCurrentUserTrialExpired,
  isSessionIndicatingTrialFocus
}: PaywallSelection): PaywallVariant {
  if (isLoggedIn && !isCurrentUserTrialExpired && (isSiteWithTrialFocus || isSessionIndicatingTrialFocus)) {
    return "authenticated-trial-focus";
  }
  if (isLoggedIn) {
    return "authenticated";
  }
  if (isSiteWithTrialFocus || isSessionIndicatingTrialFocus) {
    return "unauthenticated-trial-focus";
  }
  return "unauthenticated";
}